import { forwardRef } from 'react';
import type { PaymentRequestData } from '@saiens/shared/types';

interface PaymentRequestPdfProps {
  data: PaymentRequestData;
}

const PAGE_WIDTH = 794; // A4 at 96 DPI

const styles = {
  page: {
    width: `${PAGE_WIDTH}px`,
    padding: '40px 50px',
    fontFamily: "'Microsoft JhengHei', 'Noto Sans CJK TC', 'PingFang TC', sans-serif",
    fontSize: '13px',
    color: '#1a1a1a',
    backgroundColor: '#fff',
    lineHeight: '1.6',
  } as React.CSSProperties,
  title: {
    textAlign: 'center' as const,
    marginBottom: '24px',
  },
  companyName: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '4px',
    letterSpacing: '2px',
  } as React.CSSProperties,
  docTitle: {
    fontSize: '20px',
    fontWeight: 600,
    letterSpacing: '8px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 700,
    borderBottom: '2px solid #3b82f6',
    paddingBottom: '4px',
    marginBottom: '10px',
    marginTop: '20px',
    color: '#1e40af',
  } as React.CSSProperties,
  infoTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '16px',
  },
  infoLabel: {
    backgroundColor: '#f0f4ff',
    padding: '6px 10px',
    fontWeight: 600,
    fontSize: '12px',
    border: '1px solid #d1d5db',
    width: '15%',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  infoValue: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  contentRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '16px',
  } as React.CSSProperties,
  leftCol: {
    flex: '1 1 55%',
  } as React.CSSProperties,
  rightCol: {
    flex: '1 1 45%',
  } as React.CSSProperties,
  materialTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '12px',
  },
  th: {
    backgroundColor: '#1e40af',
    color: '#fff',
    padding: '6px 8px',
    textAlign: 'center' as const,
    fontSize: '11px',
    fontWeight: 600,
    border: '1px solid #1e40af',
  } as React.CSSProperties,
  td: {
    padding: '5px 8px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  tdRight: {
    padding: '5px 8px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  tdCenter: {
    padding: '5px 8px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  summaryRow: {
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    fontSize: '12px',
    fontWeight: 600,
    textAlign: 'right' as const,
    backgroundColor: '#f9fafb',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  balanceRow: {
    padding: '8px 8px',
    border: '2px solid #1e40af',
    fontSize: '14px',
    fontWeight: 700,
    textAlign: 'right' as const,
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  } as React.CSSProperties,
  photoItem: {
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  } as React.CSSProperties,
  photoImg: {
    width: '100%',
    height: '140px',
    objectFit: 'cover' as const,
    display: 'block',
  } as React.CSSProperties,
  photoLabel: {
    fontSize: '10px',
    color: '#6b7280',
    textAlign: 'center' as const,
    padding: '2px',
    backgroundColor: '#f9fafb',
  } as React.CSSProperties,
  declarationBox: {
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '12px',
    fontSize: '12px',
    lineHeight: '1.8',
  } as React.CSSProperties,
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    marginBottom: '4px',
  } as React.CSSProperties,
  checkIcon: {
    color: '#16a34a',
    fontWeight: 700,
    fontSize: '14px',
    flexShrink: 0,
  } as React.CSSProperties,
  bottomSection: {
    display: 'flex',
    gap: '20px',
    marginTop: '20px',
    pageBreakInside: 'avoid' as const,
  } as React.CSSProperties,
  bankBox: {
    flex: '1',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '12px 16px',
  } as React.CSSProperties,
  bankTitle: {
    fontWeight: 700,
    fontSize: '13px',
    marginBottom: '8px',
    color: '#1e40af',
  } as React.CSSProperties,
  bankRow: {
    display: 'flex',
    fontSize: '12px',
    marginBottom: '4px',
  } as React.CSSProperties,
  bankLabel: {
    width: '50px',
    fontWeight: 600,
    flexShrink: 0,
  } as React.CSSProperties,
  stampBox: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    minHeight: '120px',
  } as React.CSSProperties,
  stampImg: {
    maxHeight: '100px',
    maxWidth: '200px',
  } as React.CSSProperties,
} as const;

function formatCurrency(val: number | undefined | null): string {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString('zh-TW');
}

export const PaymentRequestPdf = forwardRef<HTMLDivElement, PaymentRequestPdfProps>(
  function PaymentRequestPdf({ data }, ref) {
    return (
      <div ref={ref} style={styles.page}>
        {/* Title */}
        <div style={styles.title}>
          <div style={styles.companyName}>{data.company_name}</div>
          <div style={styles.docTitle}>請 款 單</div>
        </div>

        {/* Section: Basic Info */}
        <div style={styles.sectionTitle}>案件資訊</div>
        <table style={styles.infoTable}>
          <tbody>
            <tr>
              <td style={styles.infoLabel}>案名</td>
              <td style={styles.infoValue}>{data.project_name || '---'}</td>
              <td style={styles.infoLabel}>客戶</td>
              <td style={styles.infoValue}>{data.partner_name || '---'}</td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>案號</td>
              <td style={styles.infoValue}>{data.sale_order_name || '---'}</td>
              <td style={styles.infoLabel}>地址</td>
              <td style={styles.infoValue}>{data.project_addr || '---'}</td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>負責業務</td>
              <td style={styles.infoValue}>{data.salesperson || '---'}</td>
              <td style={styles.infoLabel}>負責主管</td>
              <td style={styles.infoValue}>{data.coach_name || '---'}</td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>完工日期</td>
              <td style={styles.infoValue} colSpan={3}>
                {data.completion_date || '---'}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Section: Material Detail + Photos */}
        <div style={styles.sectionTitle}>材料明細</div>
        <div style={styles.contentRow}>
          {/* Left: Table */}
          <div style={styles.leftCol}>
            <table style={styles.materialTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '8%' }}>#</th>
                  <th style={{ ...styles.th, width: '37%' }}>項目</th>
                  <th style={{ ...styles.th, width: '10%' }}>數量</th>
                  <th style={{ ...styles.th, width: '20%' }}>單價</th>
                  <th style={{ ...styles.th, width: '25%' }}>金額</th>
                </tr>
              </thead>
              <tbody>
                {data.order_lines.map((line, idx) => (
                  <tr key={line.id}>
                    <td style={styles.tdCenter}>{idx + 1}</td>
                    <td style={styles.td}>{line.product_name}</td>
                    <td style={styles.tdCenter}>{line.product_uom_qty}</td>
                    <td style={styles.tdRight}>
                      {formatCurrency(line.price_unit)}
                    </td>
                    <td style={styles.tdRight}>
                      {formatCurrency(line.price_subtotal)}
                    </td>
                  </tr>
                ))}
                {/* Summary rows */}
                <tr>
                  <td colSpan={4} style={styles.summaryRow}>合計金額</td>
                  <td style={styles.summaryRow}>
                    {formatCurrency(data.amount_untaxed)} 元
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} style={styles.summaryRow}>5% 營業稅</td>
                  <td style={styles.summaryRow}>
                    {formatCurrency(data.amount_tax)} 元
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} style={styles.summaryRow}>已收訂金</td>
                  <td style={styles.summaryRow}>
                    -{formatCurrency(data.deposit_amount)} 元
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} style={styles.balanceRow}>請繳尾款</td>
                  <td style={styles.balanceRow}>
                    {formatCurrency(data.balance_amount)} 元
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Right: Photos */}
          <div style={styles.rightCol}>
            {data.photos.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    marginBottom: '6px',
                    color: '#374151',
                  }}
                >
                  精選完工照
                </div>
                <div style={styles.photoGrid}>
                  {data.photos.map((photo, idx) => (
                    <div key={idx} style={styles.photoItem}>
                      <img
                        src={photo.data_uri}
                        alt={photo.name}
                        style={styles.photoImg}
                        crossOrigin="anonymous"
                      />
                      <div style={styles.photoLabel}>{photo.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Section: Acceptance Info */}
        <div style={{ ...styles.sectionTitle, pageBreakInside: 'avoid' }}>
          驗收資訊
        </div>
        <div style={styles.declarationBox}>
          <div style={styles.checkItem}>
            <span style={styles.checkIcon}>&#9745;</span>
            <span>
              確認施工品質符合標準，施工項目已依設計圖面及客戶需求完成。
            </span>
          </div>
          <div style={styles.checkItem}>
            <span style={styles.checkIcon}>&#9745;</span>
            <span>
              完工驗收通過，同意依約定金額辦理請款。
            </span>
          </div>
          {data.items.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  marginBottom: '6px',
                }}
              >
                驗收確認項目
              </div>
              {data.items.map((item, idx) => (
                <div key={idx} style={styles.checkItem}>
                  {item.status === 'not_applicable' ? (
                    <span style={{ ...styles.checkIcon, color: '#6b7280', fontSize: '10px' }}>未施作</span>
                  ) : item.score > 0 ? (
                    <span style={styles.checkIcon}>&#9745;</span>
                  ) : (
                    <span style={{ ...styles.checkIcon, color: '#d1d5db' }}>&#9744;</span>
                  )}
                  <span style={{ fontSize: '12px' }}>{item.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signatures */}
        {data.employee_signature_uri && (
          <div
            style={{
              display: 'flex',
              gap: '20px',
              marginBottom: '16px',
              pageBreakInside: 'avoid',
            }}
          >
            {data.employee_signature_uri && (
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '8px',
                }}
              >
                <div
                  style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}
                >
                  業務簽名
                </div>
                <img
                  src={data.employee_signature_uri}
                  alt="employee signature"
                  style={{ maxHeight: '60px', maxWidth: '160px' }}
                  crossOrigin="anonymous"
                />
                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                  {data.employee_signed_date}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom: Bank Info + Company Stamp */}
        <div style={styles.bottomSection}>
          {data.bank_info && (
            <div style={styles.bankBox}>
              <div style={styles.bankTitle}>付款資訊</div>
              <div style={styles.bankRow}>
                <span style={styles.bankLabel}>戶名:</span>
                <span>{data.bank_info.acc_holder_name || '---'}</span>
              </div>
              <div style={styles.bankRow}>
                <span style={styles.bankLabel}>銀行:</span>
                <span>{data.bank_info.bank_name || '---'}</span>
              </div>
              {data.bank_info.branch_name && (
                <div style={styles.bankRow}>
                  <span style={styles.bankLabel}>分行:</span>
                  <span>{data.bank_info.branch_name}</span>
                </div>
              )}
              <div style={styles.bankRow}>
                <span style={styles.bankLabel}>帳號:</span>
                <span
                  style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                >
                  {data.bank_info.acc_number || '---'}
                </span>
              </div>
            </div>
          )}
          <div style={styles.stampBox}>
            {data.company_logo_uri ? (
              <img
                src={data.company_logo_uri}
                alt="company stamp"
                style={styles.stampImg}
                crossOrigin="anonymous"
              />
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                公司印章
              </span>
            )}
          </div>
        </div>
      </div>
    );
  },
);
